import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Skeleton, message } from "antd";
import { ReactComponent as Back } from "../../assets/svgs/back-button.svg";
import { API_URLS } from "../../api/apiUrls";
import { getApi } from "../../api/api_instance";
import { QuestionForm } from "./components/questionForm";
import TextStyles from "../../components/Elements/TextStyles/TextStyles";
import { SearchBar } from "../../components/Elements/Search/SearchBar";
import { useParams } from "react-router-dom";
import { Button } from "antd";
const scrollableContainer = {
  maxHeight: "1000px",
  overflowY: "auto",
  padding: "10px",
  borderRadius: "8px"
};

const ViewStandardFormQuestions = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const pageSize = 100;
  const navigate = useNavigate();
  const [expandAll, setExpandAll] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { formId } = useParams();
  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_STANDARD_SECTIONSBY_ID(formId)
      });
      if (response.success) {
        setSections(response.data.sections);
        if (response.data.sections.length > 0) {
          const firstSection = response.data.sections[0];
          setSelectedSection(firstSection.sectionId); // Fix
          fetchSubsections(firstSection.sectionId); // Fix
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_STANDARD_SUBSECTION_BY_ID(formId, sectionId)
      });

      if (response.success) {
        const fetchedSubsections = response.data.subsections;
        setSubsections(fetchedSubsections);
        setSelectedSection(sectionId);

        if (fetchedSubsections.length > 0) {
          const firstSubsection = fetchedSubsections[0];
          setSelectedSubsection(firstSubsection.subsectionId);

          // 👇 Immediately fetch questions for the first subsection
          fetchQuestions(firstSubsection.subsectionId, sectionId);
        } else {
          setQuestions([]); // No subsections → clear questions
          setSelectedSubsection(null);
        }
      } else {
        console.error("Failed to fetch subsections");
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = useCallback(
    async (subsectionId = selectedSubsection, sectionId = selectedSection) => {
      if (!subsectionId || !sectionId) return;
      // setQuestions([]); // Keep current questions while loading new ones to avoid flicker if preferred, but user had it cleared
      setLoading(true);
      setHasFetched(false);
      try {
        const response = await getApi({
          apiUrl: API_URLS.GET_STANDARD_SUBSECTIONQUESTIONS_BY_ID(
            formId,
            sectionId,
            subsectionId
          )
        });

        if (response.success) {
          setQuestions(response?.data?.divisions ?? []);
          setTotalQuestions(response?.data?.totalDivisionQuestion ?? 0);
        } else {
          setQuestions([]);
          setTotalQuestions(0);
        }
      } catch (error) {
        console.error("Error fetching questions", error);
        message.error("Failed to fetch questions");
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    },
    [formId, selectedSubsection, selectedSection]
  );


  // API-provided counts are used instead of local calculations

  return (
    <div style={{ backgroundColor: "#fff" }} className="h-full px-[20px] mb-6">
      <div className="flex justify-end text-xs font-light p-3">
        <p>Home</p>
        <span>/</span>

        <p>Projects</p>
        <span>/</span>
        <p>Project Detail Page</p>
        <span>/</span>
        <p>Standard Form</p>

        <span>/</span>
        <TextStyles color="gradient">All Questions</TextStyles>
      </div>
      <div className="flex justify-between pb-2">
        <div className="flex items-center">
          <Back className="cursor-pointer" onClick={() => navigate(-1)} />
          <h3 className="text-[#06513D] text-xl">All Questions</h3>
        </div>
      </div>

      <div className="w-full   shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="grid grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
          {sections.length > 0 ? (
            sections.map((section) => (
              <div
                key={section.sectionId}
                className={`
          relative
          h-10
          px-2
          flex
          items-center
          justify-center
          text-[13px]
          font-medium
          cursor-pointer
          rounded-xl
          mx-1
          my-1
          transition-all
          duration-200
          ease-in-out
          border border-[#3BA2B959]
          ${selectedSection === section.sectionId
                    ? "bg-[#06513D] text-white shadow-md transform scale-105"
                    : "bg-transparent text-gray-700 hover:text-gray-900"}
          ${selectedSection === section.section ? "" : "hover:shadow-sm"}
        `}
                onClick={() => fetchSubsections(section.sectionId)}
              >
                <span className="text-center leading-tight">
                  {section.name
                    ?.toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </span>
              </div>
            ))
          ) : (
            <p className="text-gray-500 center">No Sections available</p>
          )}
        </div>


        {selectedSection && (
          <div>
            <div className="flex flex-wrap gap-5  p-2 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
              {subsections.length > 0 ? (
                subsections.map((sub) => {
                  const isSelected = selectedSubsection === sub.subsectionId;

                  return (
                    <div
                      key={sub.subsectionId}
                      className={`p-3 rounded-xl cursor-pointer border transition-all duration-200 
                ${isSelected
                          ? "bg-[#E6EEEC]  shadow-md"
                          : "bg-transparent text-black hover:bg-gray-100 "
                        }`}
                      onClick={() => {
                        setSelectedSubsection(sub.subsectionId);
                        setExpandAll(true); // Keep divisions open by default for each subsection
                        fetchQuestions(sub.subsectionId, selectedSection);
                      }}
                    >
                      {sub.name}
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500">No subsections available</p>
              )}
            </div>
          </div>
        )}



      </div>

      <div className="w-full border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="mt-4 ml-3">
          <div className="flex items-center w-[90%] m-auto" >
            {/* Total Questions */}
            <div className="flex gap-2">
              <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-base font-light px-6">
                TOTAL QUESTIONS: {totalQuestions}
              </p>
              <SearchBar
                width="300px"
                onSearch={(value) => setSearchTerm(value)}
              />
            </div>


            {/* SearchBar in center */}
            {/* <div className="flex justify-center flex-1"> */}

            {/* </div> */}

            {/* Expand Button */}
            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-8 py-6 ml-44 mb-5"
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>

          <div style={scrollableContainer}>
            {loading ? (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
              </div>
            ) : (
              selectedSubsection && (
                <div className="mx-20 shadow-[0_0_16px_0_#3BA2B91F]">
                  {questions.length > 0 ? (
                    // <QuestionForm formId={formId} questions={questions} />
                    <QuestionForm formId={formId} questions={questions} expandAll={expandAll} />

                  ) : (
                    <p className="text-gray-500 text-center">
                      No questions available.
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewStandardFormQuestions;
